import React from 'react';
import { inject, observer } from 'mobx-react';
import { withRouter } from 'react-router-dom';
import { Button } from '@Comp';
import { FaChevronDown, FaCheckCircle, FaUpload, FaForward } from 'react-icons/fa';
import Axios from 'axios';
import { config } from '../../../constants';
import {
    Box,
    Button as MuiButton,
    CircularProgress,
    Typography,
    Modal,
    Backdrop,
    Fade,
    IconButton,
    TextField
} from '@material-ui/core';
import RefreshIcon from '@material-ui/icons/Refresh';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import CloseIcon from '@material-ui/icons/Close';
import onboardingStore from '../../../stores/onboardingStore';
import './style.scss';

const SUPPORTED_BANKS = [
    { id: 'standard', name: 'Standard Bank', apiName: 'standardBank' },
    { id: 'fnb', name: 'FNB', apiName: 'fnbBank' },
    { id: 'nedbank', name: 'Nedbank', apiName: 'nedbank' }
];

@inject('commonStore')
@withRouter
@observer
export default class OnboardingStep3 extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            verificationMethod: onboardingStore.bankData.linkMethod || null, // 'link' | 'upload' | 'skip'
            selectedBank: null,
            bankDataReady: false,
            uploadedFiles: [],
            uploadReady: false,
            showBankVerification: false,
            errorMessage: '',
            // Credential collection state
            showCredentialModal: false,
            bankEmail: '',
            bankPassword: '',
            credentialError: '',
            // Bank scraper state
            showBankModal: false,
            bankStatus: 'initial',
            bankInputPrompt: '',
            bankOtp: '',
            bankOtpError: '',
            shouldPoll: false,
            pollingInterval: null,
            // State machine for bank linking flow
            bankLinkState: 'initial', // initial | selecting | bank_selected | collecting_creds | linking | awaiting_approval | awaiting_otp | authenticated | approved | linked | failed
            bankFailureReason: null
        };
    }

    componentDidMount() {
        // Restore previous state if exists
        if (onboardingStore.bankData.linkMethod === 'link' && onboardingStore.bankData.linkedBank) {
            this.setState({
                verificationMethod: 'link',
                selectedBank: onboardingStore.bankData.linkedBank,
                bankDataReady: true,
                showBankVerification: true,
                bankLinkState: 'linked' // Restore linked state
            });
        } else if (onboardingStore.bankData.linkMethod === 'upload' && onboardingStore.bankData.uploadedFiles) {
            this.setState({
                verificationMethod: 'upload',
                uploadedFiles: onboardingStore.bankData.uploadedFiles,
                uploadReady: true
            });
        } else if (onboardingStore.bankData.linkMethod === 'skip') {
            this.setState({
                verificationMethod: 'skip'
            });
        }
    }

    componentWillUnmount() {
        // Clean up polling on unmount
        if (this.state.pollingInterval) {
            clearInterval(this.state.pollingInterval);
        }
    }

    handleMethodChange = (method) => {
        this.setState({
            verificationMethod: method,
            errorMessage: '',
            selectedBank: null,
            bankDataReady: false,
            uploadedFiles: [],
            uploadReady: false,
            showBankVerification: false,
            bankLinkState: 'initial',
            bankFailureReason: null
        });
        onboardingStore.setBankLinkMethod(method);
    };

    handleBankSelect = (e) => {
        const bankId = e.target.value;
        this.setState({
            selectedBank: bankId,
            showBankVerification: !!bankId,
            bankDataReady: false,
            errorMessage: '',
            bankLinkState: bankId ? 'bank_selected' : 'initial'
        });
    };

    handleBankDataFetched = (bankName, accountData, transactionData) => {
        console.log('Bank data fetched successfully:', { bankName, accountData, transactionData });

        // Map bank name to bank ID
        let bankId = null;
        if (bankName === 'Standard Bank') bankId = 'standard';
        else if (bankName === 'FNB Bank') bankId = 'fnb';
        else if (bankName === 'Nedbank') bankId = 'nedbank';

        // Save to onboarding store
        onboardingStore.setBankData(bankId, accountData, transactionData);

        this.setState({
            bankDataReady: true,
            errorMessage: ''
        });
    };

    // Bank scraper methods
    handleRefreshTransactions = () => {
        this.setState({
            showCredentialModal: true,
            bankEmail: '',
            bankPassword: '',
            credentialError: '',
            bankLinkState: 'collecting_creds'
        });
    };

    handleSubmitCredentials = async () => {
        const { bankEmail, bankPassword, selectedBank } = this.state;
        const { commonStore } = this.props;
        const profileId = commonStore?.businessProfile?.id;

        if (!bankEmail || !bankPassword) {
            this.setState({ credentialError: 'Please enter your bank credentials' });
            return;
        }

        try {
            // Map bank ID to bank slug format
            let bankSlug = '';
            if (selectedBank === 'standard') {
                bankSlug = 'standard bank';
            } else if (selectedBank === 'fnb') {
                bankSlug = 'fnb bank';
            } else if (selectedBank === 'nedbank') {
                bankSlug = 'nedbank';
            }

            // Save credentials
            await Axios.post(
                `${config.BANK_API_URL}/api/credentials/save`,
                {
                    profileId: profileId,
                    email: bankEmail,
                    password: bankPassword,
                    bankSlug: bankSlug
                },
                { headers: { 'X-Tenant-ID': config.TENANT_ID } }
            );

            // Close credential modal, open status modal
            this.setState({
                showCredentialModal: false,
                showBankModal: true,
                bankStatus: 'initial',
                credentialError: '',
                bankLinkState: 'linking',
                errorMessage: '', // Clear any previous errors
            });

            // Start scraping with saved credentials
            let endpoint = '';
            if (selectedBank === 'standard') {
                endpoint = `${config.BANK_API_URL}/api/standardBank/login`;
            } else if (selectedBank === 'fnb') {
                endpoint = `${config.BANK_API_URL}/api/fnbBank/login`;
            } else if (selectedBank === 'nedbank') {
                endpoint = `${config.BANK_API_URL}/api/nedbank/login`;
            }

            const response = await Axios.post(
                endpoint,
                { profile: profileId },
                { headers: { 'X-Tenant-ID': config.TENANT_ID } }
            );

            if (response.status >= 200 && response.status < 300) {
                this.startPolling(profileId);
            } else {
                this.setState({
                    bankStatus: 'failed',
                    bankInputPrompt: 'Failed to start session'
                });
            }
        } catch (error) {
            console.error('Error:', error);

            this.setState({
                credentialError: 'Something went wrong. You can skip this step.',
                showCredentialModal: true,
                showBankModal: false,
                bankLinkState: 'failed',
                bankFailureReason: 'Something went wrong. You can skip this step.'
            });
            // Don't throw - allow user to retry or skip
        }
    };

    startPolling = (profileId) => {
        const { selectedBank } = this.state;
        const pollingDelay = selectedBank === 'standard' ? 2000 : selectedBank === 'fnb' ? 3000 : 10000;

        this.setState({ shouldPoll: true });

        // Initial poll
        this.pollBankStatus(profileId);

        // Set up interval
        const interval = setInterval(() => {
            if (this.state.shouldPoll) {
                this.pollBankStatus(profileId);
            }
        }, pollingDelay);

        this.setState({ pollingInterval: interval });
    };

    pollBankStatus = async (profileId) => {
        const { selectedBank } = this.state;

        try {
            let endpoint = '';
            if (selectedBank === 'standard') {
                endpoint = `${config.BANK_API_URL}/api/standardBank/getStatus?profileId=${profileId}`;
            } else if (selectedBank === 'fnb') {
                endpoint = `${config.BANK_API_URL}/api/fnbBank/getStatus?profileId=${profileId}`;
            } else if (selectedBank === 'nedbank') {
                endpoint = `${config.BANK_API_URL}/api/nedbank/getStatus?profileId=${profileId}`;
            }

            const res = await Axios.get(endpoint, {
                headers: {
                    'X-Tenant-ID': config.TENANT_ID
                }
            });

            const { status, inputPrompt } = res.data;

            this.setState({
                bankStatus: status,
                bankInputPrompt: inputPrompt || ''
            });

            // Stop polling on terminal states
            if (status === 'approved' || status === 'failed' || status === 'error') {
                this.stopPolling();

                if (status === 'approved') {
                    // Fetch final bank data
                    await this.fetchFinalBankData(profileId);
                }
            }
        } catch (error) {
            console.error('Error polling bank status:', error);
        }
    };

    stopPolling = () => {
        if (this.state.pollingInterval) {
            clearInterval(this.state.pollingInterval);
        }
        this.setState({
            shouldPoll: false,
            pollingInterval: null
        });
    };

    fetchFinalBankData = async (profileId) => {
        const { selectedBank } = this.state;

        try {
            let endpoint = '';
            if (selectedBank === 'standard') {
                endpoint = `${config.BANK_API_URL}/api/standardBank/getData?profileId=${profileId}`;
            } else if (selectedBank === 'fnb') {
                endpoint = `${config.BANK_API_URL}/api/fnbBank/getData?profileId=${profileId}`;
            } else if (selectedBank === 'nedbank') {
                endpoint = `${config.BANK_API_URL}/api/nedbank/getData?profileId=${profileId}`;
            }

            const res = await Axios.get(endpoint, {
                headers: {
                    'X-Tenant-ID': config.TENANT_ID
                }
            });

            // Different banks have different response structures
            let data = null;
            let isSuccess = false;

            if (selectedBank === 'standard') {
                // Standard Bank returns: { success: true, data: {...} }
                isSuccess = res.data.success === true;
                data = res.data.data;
            } else {
                // FNB/Nedbank return data at root level: { _id, profile, bankName, status, accountHolderName, accounts, transaction }
                // Success is detected by presence of required fields
                data = res.data;
                isSuccess = !!(data && data.accountHolderName && data.accounts);
            }

            if (isSuccess && data) {
                // Map bank name
                let bankName = '';
                if (selectedBank === 'standard') bankName = 'Standard Bank';
                else if (selectedBank === 'fnb') bankName = 'FNB Bank';
                else if (selectedBank === 'nedbank') bankName = 'Nedbank';

                // Handle different transaction field names
                // Standard Bank/Nedbank use 'transactions' (plural)
                // FNB uses 'transaction' (singular)
                const transactions = data.transactions || data.transaction || [];

                // Call the handler
                this.handleBankDataFetched(
                    bankName,
                    data,
                    { transactions }
                );

                // Set success state
                this.setState({
                    bankDataReady: true,        // Enable Next button
                    bankLinkState: 'linked',    // Show success UI
                    showBankModal: false,       // Close modal
                    bankFailureReason: null,    // Clear any previous errors
                    errorMessage: '',           // Clear any previous errors
                });
            } else {
                // Handle failure case
                this.setState({
                    bankLinkState: 'failed',
                    bankFailureReason: 'Something went wrong. You can skip this step.',
                    showBankModal: false
                });
            }
        } catch (error) {
            console.error('Error fetching final bank data:', error);

            this.setState({
                bankLinkState: 'failed',
                bankFailureReason: 'Something went wrong. You can skip this step.',
                showBankModal: false
            });
            // Don't throw - allow user to retry or skip
        }
    };

    handleOtpSubmit = async () => {
        const { selectedBank, bankOtp } = this.state;
        const { commonStore } = this.props;
        const profileId = commonStore?.businessProfile?.id;

        if (!bankOtp || bankOtp.trim().length === 0) {
            this.setState({ bankOtpError: 'Please enter the OTP' });
            return;
        }

        try {
            let endpoint = '';
            if (selectedBank === 'standard') {
                endpoint = `${config.BANK_API_URL}/api/standardBank/submitOtp`;
            } else if (selectedBank === 'nedbank') {
                endpoint = `${config.BANK_API_URL}/api/nedbank/submitOtp`;
            }

            await Axios.post(
                endpoint,
                {
                    profileId: profileId,
                    otp: bankOtp.trim()
                },
                {
                    headers: {
                        'X-Tenant-ID': config.TENANT_ID
                    }
                }
            );

            this.setState({
                bankOtp: '',
                bankOtpError: '',
                bankStatus: 'otp-submitted'
            });
        } catch (error) {
            console.error('Error submitting OTP:', error);

            this.setState({
                bankOtpError: 'Something went wrong. Please try again.'
            });
            // Don't throw - allow user to retry
        }
    };

    handleBankModalClose = () => {
        // Only allow closing on terminal states
        const { bankStatus } = this.state;
        if (['approved', 'failed', 'error', 'authenticated'].includes(bankStatus)) {
            this.setState({ showBankModal: false });
        }
    };

    handleBankSuccess = () => {
        this.stopPolling();
        this.setState({
            showBankModal: false,
            bankStatus: 'initial'
        });
    };

    handleBankTryAgain = () => {
        this.stopPolling();
        this.setState({
            showBankModal: false,
            bankStatus: 'initial'
        });
    };

    handleUpdateBankData = () => {
        // Re-run scraping to get fresh data
        this.setState({
            showCredentialModal: true,
            bankEmail: '',
            bankPassword: '',
            credentialError: '',
            bankLinkState: 'collecting_creds',
            bankFailureReason: null
        });
    };

    handleUnlinkBank = () => {
        const { selectedBank } = this.state;

        // Clear bank data from store
        onboardingStore.setBankData(null, null, null);
        onboardingStore.setBankLinkMethod(null);

        // Reset state
        this.setState({
            verificationMethod: null,
            selectedBank: null,
            bankDataReady: false,
            bankLinkState: 'initial',
            bankFailureReason: null,
            showBankVerification: false
        });
    };

    handleRetryBankLink = () => {
        // Retry after failure
        this.setState({
            showCredentialModal: true,
            bankEmail: '',
            bankPassword: '',
            credentialError: '',
            bankLinkState: 'collecting_creds',
            bankFailureReason: null
        });
    };

    handleFileUpload = (e) => {
        const files = Array.from(e.target.files);

        try {
            // Validate file count
            if (files.length > 3) {
                this.setState({
                    errorMessage: 'Something went wrong. You can skip this step.',
                    uploadedFiles: [],
                    uploadReady: false
                });
                return;
            }

            // Validate file types
            const validFiles = files.filter(file => file.type === 'application/pdf');
            if (validFiles.length !== files.length) {
                this.setState({
                    errorMessage: 'Something went wrong. You can skip this step.',
                    uploadedFiles: [],
                    uploadReady: false
                });
                return;
            }

            // Validate file sizes (max 10MB per file)
            const maxSize = 10 * 1024 * 1024; // 10MB
            const oversizedFiles = files.filter(file => file.size > maxSize);
            if (oversizedFiles.length > 0) {
                this.setState({
                    errorMessage: 'Something went wrong. You can skip this step.',
                    uploadedFiles: [],
                    uploadReady: false
                });
                return;
            }

            // Save to store
            onboardingStore.setBankUpload(files);

            this.setState({
                uploadedFiles: files,
                uploadReady: true,
                errorMessage: ''
            });
        } catch (error) {
            console.error('Error uploading files:', error);
            this.setState({
                errorMessage: 'Something went wrong. You can skip this step.',
                uploadedFiles: [],
                uploadReady: false
            });
            // Don't throw - allow user to retry or skip
        }
    };

    handleSkip = () => {
        // Clear any errors when skipping
        this.setState({ errorMessage: '' });
        onboardingStore.skipBankLink();
        this.setState({ verificationMethod: 'skip' });
    };

    handleNext = () => {
        const { verificationMethod, bankDataReady, uploadReady } = this.state;

        if (verificationMethod === 'link' && !bankDataReady) {
            this.setState({ errorMessage: 'Please complete bank account linking' });
            return;
        }

        if (verificationMethod === 'upload' && !uploadReady) {
            this.setState({ errorMessage: 'Please upload your bank statements' });
            return;
        }

        if (!verificationMethod) {
            this.setState({ errorMessage: 'Please select a verification method' });
            return;
        }

        // Mark step complete
        onboardingStore.completeStep3();

        // Navigate to step 4
        this.props.history.push('/home/onboarding/step4');
    };

    handleBack = () => {
        this.props.history.push('/home/onboarding/step2');
    };

    isNextEnabled = () => {
        const { verificationMethod, bankDataReady, uploadReady } = this.state;

        if (verificationMethod === 'link') {
            return bankDataReady;
        }

        if (verificationMethod === 'upload') {
            return uploadReady;
        }

        if (verificationMethod === 'skip') {
            return true;
        }

        return false;
    };

    renderBankActionButton = () => {
        const { selectedBank, bankLinkState, bankFailureReason } = this.state;

        if (!selectedBank) return null;

        const bankName = SUPPORTED_BANKS.find(b => b.id === selectedBank)?.name || 'Bank';

        // State: linked - Show success UI with data summary + actions
        if (bankLinkState === 'linked') {
            return (
                <div className="bank-linked-container">
                    {this.renderBankDataSummary()}
                    <div className="bank-linked-actions">
                        <MuiButton
                            variant="outlined"
                            color="primary"
                            startIcon={<RefreshIcon />}
                            onClick={this.handleUpdateBankData}
                            style={{ marginRight: '12px' }}
                        >
                            Update Data
                        </MuiButton>
                        <MuiButton
                            variant="outlined"
                            color="secondary"
                            onClick={this.handleUnlinkBank}
                        >
                            Unlink Bank
                        </MuiButton>
                    </div>
                </div>
            );
        }

        // State: failed - Show error message + retry button + skip hint
        if (bankLinkState === 'failed') {
            return (
                <div className="bank-failed-container">
                    <Box className="error-box" style={{ marginBottom: '16px' }}>
                        <ErrorIcon style={{ color: '#f44336', marginRight: '8px', verticalAlign: 'middle' }} />
                        <Typography variant="body2" style={{ display: 'inline', color: '#f44336' }}>
                            {bankFailureReason || 'Something went wrong. You can skip this step.'}
                        </Typography>
                    </Box>
                    <MuiButton
                        variant="contained"
                        color="primary"
                        onClick={this.handleRetryBankLink}
                    >
                        Try Again
                    </MuiButton>
                </div>
            );
        }

        // State: linking - Show loading state
        if (bankLinkState === 'linking') {
            return (
                <div className="bank-linking-container">
                    <Box style={{ display: 'flex', alignItems: 'center', padding: '16px' }}>
                        <CircularProgress size={24} style={{ marginRight: '12px' }} />
                        <Typography variant="body2">Linking {bankName} account...</Typography>
                    </Box>
                </div>
            );
        }

        // State: bank_selected, initial, collecting_creds - Show link button
        return (
            <div className="bank-action-container">
                <MuiButton
                    variant="contained"
                    color="primary"
                    startIcon={<RefreshIcon />}
                    onClick={this.handleRefreshTransactions}
                    style={{ marginTop: '16px' }}
                >
                    Link {bankName} Account
                </MuiButton>
                <Typography variant="body2" style={{ marginTop: '8px', color: '#666' }}>
                    Click to securely link your {bankName} account
                </Typography>
            </div>
        );
    };

    renderBankDataSummary = () => {
        const { accountData, transactionData } = onboardingStore.bankData;

        if (!accountData) return null;

        return (
            <div className="bank-data-summary">
                <div className="summary-header">
                    <FaCheckCircle className="success-icon" />
                    <h4>Bank Account Linked Successfully</h4>
                </div>

                <div className="summary-content">
                    <div className="summary-section">
                        <h5>Account Information</h5>
                        <p><strong>Account Holder:</strong> {accountData.accountHolderName}</p>
                        {accountData.accounts && accountData.accounts.length > 0 && (
                            <div className="accounts-list">
                                <p><strong>Accounts Found:</strong> {accountData.accounts.length}</p>
                                {accountData.accounts.slice(0, 3).map((account, idx) => (
                                    <div key={idx} className="account-item">
                                        <span>{account.accountName || 'Business Account'}</span>
                                        <span className="account-number">{account.accountNumber}</span>
                                        <span className="balance">{account.availableBalance}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {transactionData && transactionData.transactions && (
                        <div className="summary-section">
                            <h5>Transactions</h5>
                            <p><strong>Total Transactions:</strong> {transactionData.transactions.length || 0}</p>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    renderCredentialModal = () => {
        const { showCredentialModal, selectedBank, bankEmail, bankPassword, credentialError } = this.state;

        if (!showCredentialModal) return null;

        const bankName = SUPPORTED_BANKS.find(b => b.id === selectedBank)?.name || 'Bank';

        return (
            <Modal
                open={showCredentialModal}
                onClose={() => this.setState({ showCredentialModal: false })}
                closeAfterTransition
                BackdropComponent={Backdrop}
                BackdropProps={{ timeout: 500 }}
            >
                <Fade in={showCredentialModal}>
                    <Box className="credential-modal-box">
                        <IconButton
                            className="modal-close-btn"
                            onClick={() => this.setState({ showCredentialModal: false })}
                            aria-label="close"
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>

                        <Typography variant="h6" className="modal-title">
                            Link {bankName} Account
                        </Typography>

                        <Typography variant="body2" className="modal-subtitle">
                            Enter your online banking credentials to securely link your account
                        </Typography>

                        {credentialError && (
                            <Box className="error-box">
                                <Typography variant="body2">{credentialError}</Typography>
                            </Box>
                        )}

                        <TextField
                            fullWidth
                            type="email"
                            label="Email / Username"
                            value={bankEmail}
                            onChange={(e) => this.setState({ bankEmail: e.target.value, credentialError: '' })}
                            className="credential-input"
                            autoFocus
                        />

                        <TextField
                            fullWidth
                            type="password"
                            label="Password"
                            value={bankPassword}
                            onChange={(e) => this.setState({ bankPassword: e.target.value, credentialError: '' })}
                            className="credential-input"
                        />

                        <Box className="security-notice">
                            <Typography variant="caption">
                                🔒 Your credentials are encrypted and used only to connect your account securely
                            </Typography>
                        </Box>

                        <Box className="modal-actions">
                            <MuiButton
                                variant="outlined"
                                onClick={() => this.setState({ showCredentialModal: false })}
                            >
                                Cancel
                            </MuiButton>
                            <MuiButton
                                variant="contained"
                                color="primary"
                                onClick={this.handleSubmitCredentials}
                                disabled={!bankEmail || !bankPassword}
                            >
                                Connect Account
                            </MuiButton>
                        </Box>
                    </Box>
                </Fade>
            </Modal>
        );
    };

    render() {
        const { verificationMethod, selectedBank, uploadedFiles, errorMessage, showBankVerification } = this.state;
        const { commonStore } = this.props;
        const profileId = commonStore?.businessProfile?.id;

        return (
            <div className="step-content">
                <h3 className="step-title">Link your business bank account</h3>
                <p className="step-description">Connect your bank to verify transactions or upload statements</p>

                {errorMessage && (
                    <div className="error-message">
                        {errorMessage}
                    </div>
                )}

                {/* Link Method Options */}
                <div className="link-options">
                    {/* Option 1: Link Bank Account */}
                    <div className={`link-option ${verificationMethod === 'link' ? 'active' : ''}`}>
                        <label className="radio-label">
                            <input
                                type="radio"
                                name="verificationMethod"
                                value="link"
                                checked={verificationMethod === 'link'}
                                onChange={() => this.handleMethodChange('link')}
                            />
                            <span className="radio-text">
                                <FaCheckCircle className="option-icon" />
                                Link your business bank account
                            </span>
                        </label>

                        {verificationMethod === 'link' && (
                            <div className="method-content">
                                <div className="bank-dropdown-container">
                                    <div className="select-wrapper">
                                        <select
                                            className="bank-select"
                                            value={selectedBank || ''}
                                            onChange={this.handleBankSelect}
                                        >
                                            <option value="">Select your bank</option>
                                            {SUPPORTED_BANKS.map(bank => (
                                                <option key={bank.id} value={bank.id}>
                                                    {bank.name}
                                                </option>
                                            ))}
                                        </select>
                                        <FaChevronDown className="dropdown-icon" />
                                    </div>
                                </div>

                                {selectedBank && profileId && (
                                    <div className="bank-verification-container">
                                        {this.renderBankActionButton()}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Option 2: Upload Statements */}
                    <div className={`link-option ${verificationMethod === 'upload' ? 'active' : ''}`}>
                        <label className="radio-label">
                            <input
                                type="radio"
                                name="verificationMethod"
                                value="upload"
                                checked={verificationMethod === 'upload'}
                                onChange={() => this.handleMethodChange('upload')}
                            />
                            <span className="radio-text">
                                <FaUpload className="option-icon" />
                                Upload 3 months bank statements
                            </span>
                        </label>

                        {verificationMethod === 'upload' && (
                            <div className="method-content">
                                <div className="upload-container">
                                    <input
                                        type="file"
                                        id="bank-statements"
                                        accept=".pdf"
                                        multiple
                                        onChange={this.handleFileUpload}
                                        style={{ display: 'none' }}
                                    />
                                    <label htmlFor="bank-statements" className="upload-button">
                                        <Button
                                            color="secondary"
                                            outline
                                            size="sm"
                                            type="button"
                                        >
                                            Choose Files
                                        </Button>
                                    </label>
                                    <div className="upload-info">
                                        {uploadedFiles.length > 0 ? (
                                            <div className="files-list">
                                                <FaCheckCircle className="success-icon-small" />
                                                <span>{uploadedFiles.length} file(s) selected</span>
                                                <ul>
                                                    {uploadedFiles.map((file, idx) => (
                                                        <li key={idx}>{file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        ) : (
                                            <span className="upload-text">No files chosen (Max 3 PDF files, 10MB each)</span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Option 3: Skip This Step */}
                    <div className={`link-option ${verificationMethod === 'skip' ? 'active' : ''}`}>
                        <label className="radio-label">
                            <input
                                type="radio"
                                name="verificationMethod"
                                value="skip"
                                checked={verificationMethod === 'skip'}
                                onChange={() => this.handleMethodChange('skip')}
                            />
                            <span className="radio-text">
                                <FaForward className="option-icon" />
                                Skip this step for now
                            </span>
                        </label>

                        {verificationMethod === 'skip' && (
                            <div className="method-content">
                                <div className="skip-info">
                                    <p>You can link your bank account later from your dashboard.</p>
                                    <p className="skip-note">Note: Linking your bank helps us process your application faster.</p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Action Buttons */}
                <div className="action-buttons">
                    <Button
                        color="secondary"
                        outline
                        onClick={this.handleBack}
                    >
                        Back
                    </Button>
                    <Button
                        color="secondary"
                        outline
                        onClick={this.handleSkip}
                    >
                        Skip
                    </Button>
                    <Button
                        color="primary"
                        onClick={this.handleNext}
                        disabled={!this.isNextEnabled()}
                    >
                        Next
                    </Button>
                </div>

                {/* Credential Collection Modal */}
                {this.renderCredentialModal()}

                {/* Bank Scraper Modal */}
                {this.renderBankScraperModal()}
            </div>
        );
    }

    renderBankScraperModal = () => {
        const { showBankModal, bankStatus, bankInputPrompt, bankOtp, bankOtpError, selectedBank } = this.state;

        if (!showBankModal) return null;

        const bankName = SUPPORTED_BANKS.find(b => b.id === selectedBank)?.name || 'Bank';

        return (
            <Modal
                open={showBankModal}
                onClose={(event, reason) => {
                    if (['approved', 'failed', 'error', 'authenticated'].includes(bankStatus)) {
                        this.handleBankModalClose();
                    }
                }}
                closeAfterTransition
                BackdropComponent={Backdrop}
                BackdropProps={{ timeout: 500 }}
            >
                <Fade in={showBankModal}>
                    <Box
                        style={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: 400,
                            backgroundColor: 'white',
                            borderRadius: 8,
                            padding: 24,
                            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                            outline: 'none'
                        }}
                    >
                        {/* Close Icon */}
                        <IconButton
                            onClick={this.handleBankModalClose}
                            style={{
                                position: 'absolute',
                                top: 8,
                                right: 8,
                                padding: 8,
                                color: '#666'
                            }}
                            aria-label="close"
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>

                        {/* Initial Status */}
                        {bankStatus === 'initial' && (
                            <>
                                <Box style={{ textAlign: 'center', marginBottom: 16 }}>
                                    <CircularProgress />
                                </Box>
                                <Typography variant="h6" style={{ marginBottom: 8, textAlign: 'center' }}>
                                    Please wait...
                                </Typography>
                                <Typography variant="body2" style={{ textAlign: 'center', color: '#666' }}>
                                    Initializing connection with {bankName}
                                </Typography>
                            </>
                        )}

                        {/* Pending Status */}
                        {bankStatus === 'pending' && (
                            <>
                                <Box style={{ textAlign: 'center', marginBottom: 16 }}>
                                    <CircularProgress />
                                </Box>
                                <Typography variant="h6" style={{ marginBottom: 8, textAlign: 'center' }}>
                                    Connecting to {bankName}...
                                </Typography>
                                <Typography variant="body2" style={{ textAlign: 'center', color: '#666' }}>
                                    Please wait while we establish a secure connection
                                </Typography>
                            </>
                        )}

                        {/* In Progress (FNB specific) */}
                        {bankStatus === 'in-progress' && (
                            <>
                                <Box style={{ textAlign: 'center', marginBottom: 16 }}>
                                    <CircularProgress />
                                </Box>
                                <Typography variant="h6" style={{ marginBottom: 8, textAlign: 'center' }}>
                                    Scraping {bankName} data...
                                </Typography>
                                <Typography variant="body2" style={{ textAlign: 'center', color: '#666' }}>
                                    {bankInputPrompt || 'Please wait while we fetch your bank data'}
                                </Typography>
                            </>
                        )}

                        {/* Awaiting Approval Status */}
                        {bankStatus === 'awaiting-approval' && (
                            <>
                                <Box style={{ textAlign: 'center', marginBottom: 16 }}>
                                    <CircularProgress />
                                </Box>
                                <Typography variant="h6" style={{ marginBottom: 16, textAlign: 'center' }}>
                                    Awaiting Approval
                                </Typography>
                                <Typography variant="body2" style={{ textAlign: 'center', color: '#666' }}>
                                    {bankInputPrompt || 'Please approve the login request from your mobile device'}
                                </Typography>
                            </>
                        )}

                        {/* Awaiting OTP Status */}
                        {bankStatus === 'awaiting-otp' && (
                            <>
                                <Typography variant="h6" style={{ marginBottom: 16, textAlign: 'center' }}>
                                    Enter OTP
                                </Typography>
                                <Typography variant="body2" style={{ marginBottom: 16, textAlign: 'center', color: '#666' }}>
                                    {bankInputPrompt || 'Please enter the OTP sent to your mobile'}
                                </Typography>
                                <TextField
                                    fullWidth
                                    type="text"
                                    label="OTP"
                                    value={bankOtp}
                                    onChange={(e) => {
                                        const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                                        this.setState({ bankOtp: value, bankOtpError: '' });
                                    }}
                                    error={!!bankOtpError}
                                    helperText={bankOtpError}
                                    autoFocus
                                    inputProps={{
                                        maxLength: 6,
                                        style: { textAlign: 'center', fontSize: 24, letterSpacing: 8 }
                                    }}
                                />
                                <Box style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
                                    <MuiButton
                                        variant="contained"
                                        color="primary"
                                        onClick={this.handleOtpSubmit}
                                        disabled={bankOtp.length === 0}
                                    >
                                        Submit
                                    </MuiButton>
                                </Box>
                            </>
                        )}

                        {/* OTP Submitted Status */}
                        {bankStatus === 'otp-submitted' && (
                            <>
                                <Box style={{ textAlign: 'center', marginBottom: 16 }}>
                                    <CircularProgress />
                                </Box>
                                <Typography variant="h6" style={{ marginBottom: 8, textAlign: 'center' }}>
                                    Verifying OTP...
                                </Typography>
                                <Typography variant="body2" style={{ textAlign: 'center', color: '#666' }}>
                                    Please wait
                                </Typography>
                            </>
                        )}

                        {/* Authenticated Status */}
                        {bankStatus === 'authenticated' && (
                            <>
                                <Box style={{ textAlign: 'center', marginBottom: 16 }}>
                                    <CircularProgress />
                                </Box>
                                <Typography variant="h6" style={{ marginBottom: 16, textAlign: 'center' }}>
                                    Retrieving transactions...
                                </Typography>
                                <Typography variant="body2" style={{ textAlign: 'center', color: '#666', marginBottom: 16 }}>
                                    This may take a few moments. You can close now and come back later or wait.
                                </Typography>
                                <Box style={{ display: 'flex', justifyContent: 'center' }}>
                                    <MuiButton variant="outlined" onClick={this.handleBankModalClose}>
                                        Close and wait
                                    </MuiButton>
                                </Box>
                            </>
                        )}

                        {/* Approved Status */}
                        {bankStatus === 'approved' && (
                            <>
                                <Box style={{ textAlign: 'center', marginBottom: 16 }}>
                                    <CheckCircleIcon style={{ fontSize: 60, color: '#4caf50' }} />
                                </Box>
                                <Typography variant="h6" style={{ marginBottom: 16, textAlign: 'center', color: '#4caf50' }}>
                                    Success!
                                </Typography>
                                <Typography variant="body2" style={{ textAlign: 'center', color: '#666', marginBottom: 24 }}>
                                    Bank data fetched successfully
                                </Typography>
                                <Box style={{ display: 'flex', justifyContent: 'center' }}>
                                    <MuiButton
                                        variant="contained"
                                        style={{ backgroundColor: '#4caf50', color: 'white' }}
                                        onClick={this.handleBankSuccess}
                                    >
                                        Done
                                    </MuiButton>
                                </Box>
                            </>
                        )}

                        {/* Error Status */}
                        {bankStatus === 'error' && (
                            <>
                                <Box style={{ textAlign: 'center', marginBottom: 16 }}>
                                    <ErrorIcon style={{ fontSize: 60, color: '#f44336' }} />
                                </Box>
                                <Typography variant="h6" style={{ marginBottom: 16, textAlign: 'center', color: '#f44336' }}>
                                    Error
                                </Typography>
                                <Typography variant="body2" style={{ textAlign: 'center', color: '#666', marginBottom: 24 }}>
                                    {bankInputPrompt || 'Something went wrong. Please try again later.'}
                                </Typography>
                                <Box style={{ display: 'flex', justifyContent: 'center' }}>
                                    <MuiButton
                                        variant="contained"
                                        style={{ backgroundColor: '#f44336', color: 'white' }}
                                        onClick={this.handleBankTryAgain}
                                    >
                                        OK
                                    </MuiButton>
                                </Box>
                            </>
                        )}

                        {/* Failed Status */}
                        {bankStatus === 'failed' && (
                            <>
                                <Box style={{ textAlign: 'center', marginBottom: 16 }}>
                                    <ErrorIcon style={{ fontSize: 60, color: '#f44336' }} />
                                </Box>
                                <Typography variant="h6" style={{ marginBottom: 16, textAlign: 'center', color: '#f44336' }}>
                                    Authentication Failed
                                </Typography>
                                <Typography variant="body2" style={{ textAlign: 'center', color: '#666', marginBottom: 24 }}>
                                    {bankInputPrompt || 'Invalid credentials. Please try again.'}
                                </Typography>
                                <Box style={{ display: 'flex', justifyContent: 'center' }}>
                                    <MuiButton
                                        variant="contained"
                                        style={{ backgroundColor: '#f44336', color: 'white' }}
                                        onClick={this.handleBankTryAgain}
                                    >
                                        OK
                                    </MuiButton>
                                </Box>
                            </>
                        )}
                    </Box>
                </Fade>
            </Modal>
        );
    };
}
